import { Box, Flex, HStack, Image, Input, Stack, Text } from "@chakra-ui/react"
import type { ChangeEvent, ComponentType, FC, ReactNode } from "react"
import { useEffect, useState, useMemo } from "react"
import type { ControlProps, OnChangeValue, OptionProps } from "react-select"
import { components } from "react-select"
import BigNumber from "bignumber.js"
import { formatUnits } from "ethers/lib/utils.js"

import { formatDollars } from "common/helpers/formatDollars"
import { getWeightLabel } from "common/helpers/bignumber"
import { CustomSelect } from "common/components/CustomSelect"

export type AssetForSelector = {
  address: string
  balance?: string
  name?: string
  decimals?: number | string
  logo?: string
  quoteRate?: number | null
  symbol: string
}

type AssetSelectorWithInputProps = {
  amount?: string
  defaultAmount?: string
  assets: AssetForSelector[]
  footer?: ReactNode
  isDisabled?: boolean
  isInputDisabled?: boolean
  selectPlaceholder: string
  selectedAsset?: AssetForSelector
  withBalance?: boolean
  onChangeAmount: (v: string) => void
  onChangeAsset: (a: AssetForSelector) => void
}

const AssetSelectorWithInput: FC<AssetSelectorWithInputProps> = ({
  amount,
  defaultAmount,
  assets,
  footer,
  isDisabled = false,
  isInputDisabled = false,
  selectPlaceholder,
  selectedAsset,
  withBalance = false,
  onChangeAmount,
  onChangeAsset,
}) => {
  const decimals = Number(selectedAsset?.decimals) || 18

  const [value, setValue] = useState(defaultAmount ?? "")
  const [isBlur, setIsBlur] = useState(false)

  useEffect(() => {
    if (isInputDisabled) return

    if (!Number(value)) {
      onChangeAmount("")

      return
    }

    const bigValue = new BigNumber(value)

    const bigDecimals = new BigNumber(10).pow(decimals)
    const bigValueInWei = bigValue.multipliedBy(bigDecimals)

    onChangeAmount(bigValueInWei.toFixed())
  }, [isInputDisabled, value, decimals, onChangeAmount])

  const handleChangeAmount = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (
      new BigNumber(value).isNaN() && // prevent non-numerical values
      value !== "" // allow clearing input
    ) {
      return
    }

    setValue(value)
  }

  const formattedAmount = useMemo(() => {
    if (!amount) return

    return formatUnits(amount, decimals)
  }, [amount, decimals])

  const usdAmount = useMemo(() => {
    if (!selectedAsset) return 0

    const { quoteRate } = selectedAsset

    if (!quoteRate) return

    if (isInputDisabled && formattedAmount) {
      return Number(formattedAmount) * quoteRate
    }

    return Number(value) * quoteRate
  }, [selectedAsset, isInputDisabled, formattedAmount, value])

  const handleChange = (newValue: OnChangeValue<AssetForSelector, false>) => {
    if (!newValue) return

    onChangeAsset(newValue)
  }

  const shouldDisplayBalance = withBalance && selectedAsset

  const isError = !value && isBlur

  const optionAssets = assets.map((asset) => {
    if (!Boolean(asset.symbol)) {
      return { ...asset, symbol: asset.name ?? "" } as AssetForSelector
    }

    return asset as AssetForSelector
  })

  return (
    <Box
      border={isError ? "2px solid red" : "gray.200"}
      borderRadius="sm"
      fontSize="sm"
      fontWeight="medium"
    >
      <Flex justify="space-between" px="2" py="3">
        <Stack align="flex-start" color="gray.700">
          {isInputDisabled ? (
            <Text fontWeight="bold" textStyle="h4">
              ~{formattedAmount}
            </Text>
          ) : (
            <Input
              border="none"
              disabled={isInputDisabled}
              focusBorderColor="transparent"
              fontSize="4xl"
              fontWeight="bold"
              p={0}
              placeholder="0"
              sx={{
                "::placeholder": {
                  color: "gray.400",
                },
              }}
              textAlign="left"
              value={value}
              onBlur={() => setIsBlur(true)}
              onChange={handleChangeAmount}
            />
          )}

          {value ? (
            <Text color="gray.500">
              {usdAmount
                ? `$${formatDollars(usdAmount).formattedDollars}.${
                    formatDollars(usdAmount).formattedCents
                  }`
                : "USD quote unavailable"}
            </Text>
          ) : null}

          {!value ? (
            <Text color={isError ? "red.500" : "gray.500"}>Enter amount</Text>
          ) : null}
        </Stack>

        <Stack spacing={3}>
          <CustomSelect<AssetForSelector, false>
            components={{
              Control,
              IndicatorSeparator: null,
              Option: Option as any,
            }}
            getOptionLabel={(option) =>
              Boolean(option.symbol) ? option.symbol : (option.name as string)
            }
            getOptionValue={(option) =>
              Boolean(option.symbol) ? option.symbol : (option.name as string)
            }
            isDisabled={isDisabled}
            options={optionAssets}
            placeholder={selectPlaceholder}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                minWidth: "125px",
                borderColor: "#E2E8F0", // gray.200
              }),
            }}
            value={selectedAsset}
            onChange={handleChange}
          />
          {shouldDisplayBalance && (
            <Text align="right" color="gray.500">
              Balance:{" "}
              {getWeightLabel(
                selectedAsset?.balance || "0",
                Number(selectedAsset.decimals),
              )}
            </Text>
          )}
        </Stack>
      </Flex>
      {footer && (
        <Box
          border="gray.light"
          borderBottom="none"
          borderLeft="none"
          borderRight="none"
          px="2"
          py="3"
        >
          {footer}
        </Box>
      )}
    </Box>
  )
}

export default AssetSelectorWithInput

const Control: FC<ControlProps<AssetForSelector, false>> = ({
  children,
  ...props
}) => {
  const value = props.getValue()

  const [selectedAsset] = value

  return (
    <components.Control {...props}>
      {selectedAsset && selectedAsset.logo ? (
        <Image
          alt={selectedAsset.symbol}
          background="gray.300"
          borderRadius="full"
          boxSize="1.5rem"
          fallbackSrc="https://via.placeholder.com/1"
          ml="1"
          src={selectedAsset.logo}
        />
      ) : null}
      {children}
    </components.Control>
  )
}

const Option: ComponentType<OptionProps<AssetForSelector, boolean>> = (
  props,
) => {
  const { data } = props
  const { logo, symbol } = data

  return (
    <components.Option {...props}>
      <HStack>
        {logo ? (
          <Image
            alt={symbol}
            background="gray.300"
            borderRadius="full"
            boxSize="1.5rem"
            fallbackSrc="https://via.placeholder.com/1"
            ml="1"
            src={logo}
            onError={() => console.log("error")}
          />
        ) : null}
        <Text>{symbol}</Text>
      </HStack>
    </components.Option>
  )
}
